"use client";

import { userControllerGetProfileQueryKey, useUserControllerGetProfile, useUserPreferencesControllerUpdateUserPreferences } from "@dc/gateway-jsonapi";
import { addToast } from "@dc/ui/hooks/addToast";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslations } from "next-intl";
import { useCallback, useRef, useState } from "react";
import { USER_PROFILE_QUERY_PARAMS } from "@/lib/user";

/**
 * Hook to get the current user profile and manage user preferences
 */
export function useMe() {
  const t = useTranslations("preferences");
  const queryClient = useQueryClient();
  const {
    data: me,
    error,
    isPending,
    isFetching,
    refetch
  } = useUserControllerGetProfile(USER_PROFILE_QUERY_PARAMS);
  const [isUpdating, setIsUpdating] = useState(false);
  const [optimisticallyDismissedUserId, setOptimisticallyDismissedUserId] = useState<string | null>(null);
  const {
    mutateAsync: updateUserPreferences
  } = useUserPreferencesControllerUpdateUserPreferences({
    mutation: {
      meta: {
        skipInvalidation: true
      }
    }
  });
  const userPreferences = me?.included?.find(item => item.type === "user-preferences");
  const userId = me?.data?.id;
  const isSkillsEducationDismissed = optimisticallyDismissedUserId === userId;
  const principalRelationship = me?.data?.relationships?.principal;
  const principalId = principalRelationship && "data" in principalRelationship && principalRelationship.data ? (principalRelationship.data as {
    id: string;
  }).id : undefined;
  const currentTeamId = (userPreferences?.attributes as {
    currentTeamId?: string | null;
  } | undefined)?.currentTeamId ?? null;
  const language = (userPreferences?.attributes as {
    language?: string | null;
  } | undefined)?.language ?? null;
  const skillsEducationDismissedAt = (userPreferences?.attributes as {
    skillsEducationDismissedAt?: string | null;
  } | undefined)?.skillsEducationDismissedAt;
  const wasMigratedFromDc1 = me?.meta?.wasMigratedFromDc1 ?? false;
  const isMigrated = wasMigratedFromDc1;
  const currentTeamIdRef = useRef(currentTeamId);
  const userIdRef = useRef(userId);
  const updateUserPreferencesRef = useRef(updateUserPreferences);
  const queryClientRef = useRef(queryClient);
  const settingTeamIdRef = useRef<string | null>(null);
  currentTeamIdRef.current = currentTeamId;
  userIdRef.current = userId;
  updateUserPreferencesRef.current = updateUserPreferences;
  queryClientRef.current = queryClient;
  if (settingTeamIdRef.current && currentTeamId === settingTeamIdRef.current) {
    settingTeamIdRef.current = null;
  }
  const setCurrentTeam = useCallback(async (teamId: string) => {
    if (!userIdRef.current) {
      console.warn("Cannot set current team: userId is not available");
      return;
    }
    if (currentTeamIdRef.current === teamId) {
      return;
    }
    const payload = {
      data: {
        type: "user-preferences" as const,
        id: userIdRef.current,
        attributes: {
          currentTeamId: teamId
        }
      }
    };
    setIsUpdating(true);
    settingTeamIdRef.current = teamId;
    try {
      await updateUserPreferencesRef.current({
        id: userIdRef.current,
        data: payload
      });

      // Optimistically update cache with new currentTeamId to prevent flicker on navigation
      queryClientRef.current.setQueryData(userControllerGetProfileQueryKey(USER_PROFILE_QUERY_PARAMS), (oldData: typeof me) => {
        if (!oldData) return oldData;
        return {
          ...oldData,
          included: oldData.included?.map(item_0 => item_0.type === "user-preferences" ? {
            ...item_0,
            attributes: {
              ...item_0.attributes,
              currentTeamId: teamId
            }
          } : item_0)
        };
      });

      // Invalidate to ensure server data syncs in background
      queryClientRef.current.invalidateQueries({
        queryKey: userControllerGetProfileQueryKey(USER_PROFILE_QUERY_PARAMS)
      });
    } catch (err) {
      settingTeamIdRef.current = null;
      const errorMessage = err instanceof Error ? err.message : "Failed to switch team";
      console.error("Failed to update current team:", err);
      addToast({
        title: t("error_title"),
        description: t("switch_team_failed"),
        color: "danger"
      });
      throw new Error(errorMessage);
    } finally {
      setIsUpdating(false);
    }
  }, [t]);
  const setLanguage = useCallback(async (newLanguage: string) => {
    if (!userIdRef.current) {
      console.warn("Cannot set language: userId is not available");
      return;
    }
    const payload_0 = {
      data: {
        type: "user-preferences" as const,
        id: userIdRef.current,
        attributes: {
          language: newLanguage
        }
      }
    };
    try {
      await updateUserPreferencesRef.current({
        id: userIdRef.current,
        data: payload_0
      });
      queryClientRef.current.setQueryData(userControllerGetProfileQueryKey(USER_PROFILE_QUERY_PARAMS), (oldData_0: typeof me) => {
        if (!oldData_0) return oldData_0;
        return {
          ...oldData_0,
          included: oldData_0.included?.map(item_1 => item_1.type === "user-preferences" ? {
            ...item_1,
            attributes: {
              ...item_1.attributes,
              language: newLanguage
            }
          } : item_1)
        };
      });
      queryClientRef.current.invalidateQueries({
        queryKey: userControllerGetProfileQueryKey(USER_PROFILE_QUERY_PARAMS)
      });
    } catch (err_0) {
      console.error("Failed to update language preference:", err_0);
      addToast({
        title: t("error_title"),
        description: t("update_language_failed"),
        color: "danger"
      });
    }
  }, [t]);
  const dismissSkillsEducation = useCallback(async () => {
    const preferenceId = userIdRef.current;
    if (!preferenceId) return;
    setOptimisticallyDismissedUserId(preferenceId);
    // Codegen omits data.id, but JsonApiBodyPipe requires it for update DTOs.
    const requestData = {
      data: {
        type: "user-preferences" as const,
        id: preferenceId,
        attributes: {
          skillsEducationDismissed: true as const
        }
      }
    };
    try {
      const response = await updateUserPreferencesRef.current({
        id: preferenceId,
        data: requestData
      });
      const dismissedAt = response.data.attributes.skillsEducationDismissedAt;
      if (!dismissedAt) throw new Error("Dismissal response did not include a timestamp");
      queryClientRef.current.setQueryData(userControllerGetProfileQueryKey(USER_PROFILE_QUERY_PARAMS), (oldData_1: typeof me) => {
        if (!oldData_1 || oldData_1.data.id !== preferenceId) return oldData_1;
        return {
          ...oldData_1,
          included: oldData_1.included?.map(item_2 => item_2.type === "user-preferences" ? {
            ...item_2,
            attributes: {
              ...item_2.attributes,
              skillsEducationDismissedAt: dismissedAt
            }
          } : item_2)
        };
      });
      void queryClientRef.current.invalidateQueries({
        queryKey: userControllerGetProfileQueryKey(USER_PROFILE_QUERY_PARAMS)
      });
    } catch (error_0) {
      setOptimisticallyDismissedUserId(dismissedUserId => dismissedUserId === preferenceId ? null : dismissedUserId);
      console.error("Failed to dismiss Skills education", {
        error: error_0 instanceof Error ? error_0.message : String(error_0)
      });
      addToast({
        title: t("dismiss_skills_education_failed"),
        description: t("try_again"),
        color: "danger"
      });
    }
  }, [t]);
  return {
    me,
    userId,
    principalId,
    isMigrated,
    preferences: userPreferences?.attributes,
    currentTeamId,
    language,
    skillsEducationDismissedAt,
    isSkillsEducationDismissed,
    wasMigratedFromDc1,
    setCurrentTeam,
    setLanguage,
    dismissSkillsEducation,
    isUpdating: isUpdating || settingTeamIdRef.current !== null,
    error,
    // Only consider it "loading" on initial fetch, not on background refetches
    // This prevents components from showing loading states during refetches
    isLoading: isPending,
    isFetching,
    refetch
  };
}